import { useCallback } from 'react';
import { extend } from 'lodash';
import { useInfrastructureErrorHandler } from '../infrastructure/hooks/UseInfrastructureErrorHandler';
import { createBaseDetailObject } from '../infrastructure/utils/InfrastructureDefaults';
import { createBooleanFilterDrillDownBuilder } from '../infrastructure/utils/DrillDownUtils';
import { getSystemTime } from '../../../stores/SystemTimeStore';
import {
    ACCESSPOINT,
    ACCESSPOINT_FILTER_ID,
    DESCRIPTION_FIELD,
    DEVICE_DETAIL_ACTION_URLS,
    DEVICE_FILTER_ID,
    FIREWALL,
    GENERIC,
    INSERT_TIME,
    IPADDRESS_FIELD,
    MANUAL_NAME,
    MONITORED_INFRASTRUCTURE_DETAIL_FUNCTION_NAME,
    MONITORED_INFRASTRUCTURE_TITLE,
    NAME_FIELD,
    NEW_BREAD_CRUMB_TEXT,
    PACKET_PROCESSOR,
    ROUTER,
    SERVER,
    SERVER_FILTER_ID,
    SWITCH,
    WIRELESSCONTROLLER,
    WIRELESSCONTROLLER_FILTER_ID,
} from '../infrastructure/InfrastructureConstants';
import InfrastructureDetailBase from '../infrastructure/InfrastructureDetailBase';
import { buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import DeviceDetailSection from '../infrastructure/device/DeviceDetailSection';
import DeviceTablesSection from '../infrastructure/device/DeviceTablesSection';
import DeviceDetailActionButtons from '../infrastructure/device/DeviceDetailActionButtons';

const GO_TO_URL = '../';
const BASE_URL = '/ipm/v1/admin/monitoredDeviceConfig/';

const DEFAULT_VALUES = {
    [NAME_FIELD]: '',
    [IPADDRESS_FIELD]: '',
    [DESCRIPTION_FIELD]: '',
};

const DEVICE_NEW_DETAIL_OBJECT = createBaseDetailObject();

const DRILL_DOWN_PAGES = {
    [ROUTER]: 'device-detail',
    [SWITCH]: 'device-detail',
    [FIREWALL]: 'device-detail',
    [PACKET_PROCESSOR]: 'device-detail',
    [GENERIC]: 'device-detail',
    [SERVER]: 'server-detail',
    [WIRELESSCONTROLLER]: 'wireless-controller-detail',
    [ACCESSPOINT]: 'access-point-detail',
};

const DRILL_DOWN_FILTERS = {
    [ROUTER]: DEVICE_FILTER_ID,
    [SWITCH]: DEVICE_FILTER_ID,
    [FIREWALL]: DEVICE_FILTER_ID,
    [PACKET_PROCESSOR]: DEVICE_FILTER_ID,
    [GENERIC]: DEVICE_FILTER_ID,
    [SERVER]: SERVER_FILTER_ID,
    [WIRELESSCONTROLLER]: WIRELESSCONTROLLER_FILTER_ID,
    [ACCESSPOINT]: ACCESSPOINT_FILTER_ID,
};

const getTargetConfig = createBooleanFilterDrillDownBuilder(DRILL_DOWN_PAGES, DRILL_DOWN_FILTERS);

const FUNCTION_BREADCRUMBS = [{ link: GO_TO_URL, text: MONITORED_INFRASTRUCTURE_TITLE }];

export default function MonitoredInfrastructureDetail() {
    const recordId = useEntityIdParam();
    const dataTableActionErrorHandler = useInfrastructureErrorHandler();

    const createSubmitTransform = useCallback((dataToSave) => {
        const newDataToSave = extend({}, DEVICE_NEW_DETAIL_OBJECT, dataToSave);
        newDataToSave[MANUAL_NAME] = dataToSave[MANUAL_NAME] || dataToSave[NAME_FIELD];
        newDataToSave[INSERT_TIME] = getSystemTime().valueOf();
        newDataToSave.insertedAt = getSystemTime().toISOString();
        return newDataToSave;
    }, []);

    const renderActionButtons = useCallback(
        (actionProps) => {
            return <DeviceDetailActionButtons {...actionProps} recordId={recordId} />;
        },
        [recordId]
    );

    const renderFormContent = useCallback(
        () => (
            <>
                <DeviceDetailSection />
                <DeviceTablesSection />
            </>
        ),
        []
    );

    return (
        <InfrastructureDetailBase
            recordId={recordId}
            showSaveButton
            tableSelectionAction
            baseUrl={BASE_URL}
            goToUrl={GO_TO_URL}
            defaultValues={DEFAULT_VALUES}
            pageId={buildPageId(MONITORED_INFRASTRUCTURE_DETAIL_FUNCTION_NAME)}
            nameField={NAME_FIELD}
            typeField="deviceType"
            newRecordBreadcrumbText={NEW_BREAD_CRUMB_TEXT}
            functionBreadcrumbs={FUNCTION_BREADCRUMBS}
            actionUrls={DEVICE_DETAIL_ACTION_URLS}
            getTargetConfig={getTargetConfig}
            actionButtonsFn={renderActionButtons}
            errorHandler={dataTableActionErrorHandler}
            createSubmitTransform={createSubmitTransform}
        >
            {renderFormContent}
        </InfrastructureDetailBase>
    );
}
